import route from '../../../route.config'
import {
  toPascalCase,
} from '../../utils'

// 路由权限白名单 & 子路由父级映射
// --------------------------------------------------------------------
// 背景：原实现 (commit 前) 会把 route.config 里所有未匹配到用户菜单的路由也
// 注册进 <Route>，导致用户仅凭 URL 就能越权访问 `/staff_manage` / `/role_manage`
// 等管理页面。
//
// 修复策略：
//   1. 菜单里的路由：照常注册
//   2. 公共路由（HOME 等）：无条件注册
//   3. 子路由（新增 / 编辑 / 详情等）：只在对应父菜单有权限时注册
//
// 子路由父级关系通过 SUB_ROUTE_PARENT 显式声明，基于 saas_user.menu 表交叉比对
// （见 2026-04-17 修复记录）。如果未来 route.config 新增 route 但忘了登记父级，
// 会走 fallback 首段前缀启发——如果启发也命不中，就视为无权限不注册。
//
// 核心提醒：前端路由守卫只是 UX 层防护。API 侧必须独立校验权限，那才是安全底线。

// 无需任何菜单权限即可访问的公共路由
const PUBLIC_ROUTE_KEYS = new Set<string>([
  'HOME',
  'NEWS_CENTER'
])

// 子路由 → 必需父菜单 key。父菜单在用户菜单中时，子路由才注册
// 数据来源：saas_user.menu + route.config.ts 交叉比对
const SUB_ROUTE_PARENT: Record<string, string> = {
  // 款式
  STYLE_ADDITION: 'STYLE_CENTER',
  STYLE_EDITOR: 'STYLE_CENTER',
  STYLE_DETAILS: 'STYLE_CENTER',
  // 入库 - 仓库柜组 / 属性
  CABINET_MANAGE: 'WAREHOUSE_MANAGE',
  FEATURE_VALUE_MANAGE: 'FEATURE_MANAGE',
  // 出库
  CREATE_IMPORT_STORAGE_OUT: 'CREATE_STORAGE_OUT',
  // 小程序 - 商品
  SELLER_COMMODITY_EDIT: 'SELLER_COMMODITY_MANAGE',
  SELLER_COMMODITY_SPECIAL_ADD: 'SELLER_COMMODITY_SPECIAL',
  SELLER_COMMODITY_SPECIAL_EDIT: 'SELLER_COMMODITY_SPECIAL',
  // 小程序 - 营销
  SELLER_ADD_COUPON: 'SELLER_COUPON',
  EDIT_COUPON: 'SELLER_COUPON',
  ACTIVITY_MENU_ADD: 'ACTIVITY_MENU',
  ACTIVITY_MENU_EDIT: 'ACTIVITY_MENU',
  // 小程序 - 用户
  SELLER_DISTRIBUTOR_DETAIL: 'SELLER_DISTRIBUTOR',
  // 选品
  SELECTION_COMMODITY_ADD: 'SELLER_CHOOSE_MINE_COMMODITY',
  SELECTION_COMMODITY_EDIT: 'SELLER_CHOOSE_MINE_COMMODITY',
  // 运营 - 平台
  PLATFORM_CHOOSE_FEATURE_MANAGE: 'PLATFORM_CHOOSE_FEATURE',
  // 基础 - 单据模板
  DOCUMENT_LABEL_SETTING_DETAILS: 'DOCUMENT_LABEL_SETTING',
  // 数据 / 跨境 - EDATA
  EDATA_SALES_EMAIL_TEMPLATE_DETAIL: 'EDATA_SALES_EMAIL_TEMPLATE',
  EDATA_SALES_EMAIL_TEMPLATE_DETAILTWO: 'EDATA_SALES_EMAIL_TEMPLATE',
  EDATA_WORD_TOP: 'EDATA_ITEM_DATA',
  EDATA_SHOP_KEYWORD_ANALYSIS: 'EDATA_SHOP_DATA',
  PRODUCT_SELECT_WORD: 'PRODUCT_DATA',
  // 入库 - 供应商详情
  SUPPLIER_FACTORY_DETAIL_DATA: 'SUPPLIER_MANAGE',
  // 跨境 - Shopify 商品下沉
  SHOPIFY_PRODUCT_EDIT: 'SHOPIFY_PRODUCT_MANAGE',
  SHOPIFY_PRODUCT_CREATE: 'SHOPIFY_PRODUCT_MANAGE',
}

function isRouteAllowed(
  routeKey: string,
  allowedKeys: Set<string>,
): boolean {
  if (PUBLIC_ROUTE_KEYS.has(routeKey)) return true
  if (allowedKeys.has(routeKey)) return true

  // 显式父级声明
  const explicitParent = SUB_ROUTE_PARENT[routeKey]
  if (explicitParent) {
    return allowedKeys.has(explicitParent)
  }

  // fallback：首段前缀启发（STYLE_EDITOR 首段 STYLE，若菜单有 STYLE_* 即通过）
  // 覆盖 SUB_ROUTE_PARENT 中没显式声明的未知子路由，但仅在「首段完全一致」时允许，
  // 不跨前缀相似（避免 SELLER_ 前缀共享一刀切）
  const firstSeg = routeKey.split('_')[0]
  for (const ak of allowedKeys) {
    if (ak.split('_')[0] === firstSeg) return true
  }
  return false
}

export default function(menus = []) {
  function getDefault(v) {
    return {
      help: true,
      component: toPascalCase(v.key),
      path: `/${v.key.toLowerCase()}`,
    }
  }
  let newArr = [];
  function arrOfOneDimension(arr) {
    for (let key of arr) {
      if (key?.children?.length > 0) {
        arrOfOneDimension(key?.children);
      } else {
        newArr.push(key);
      }
    }
  }
  arrOfOneDimension(menus)

  const allowedKeys = new Set<string>(newArr.map((v: any) => v.key as string))

  // 1) 菜单里的路由
  const menuRoutes = newArr.map(v => {
    const rc = route.find(r => r.key === v.key)
    return {
      ...getDefault(v),
      ...v,
      ...rc,
    }
  })

  // 2) route.config 里不在菜单，但「公共 or 子路由且父菜单有权限」的路由
  const extraRoutes = route
    .filter(v => !allowedKeys.has(v.key) && isRouteAllowed(v.key, allowedKeys))
    .map(v => ({
      ...getDefault(v),
      ...v,
    }))

  return menuRoutes.concat(extraRoutes)
}
